/** 与 h5-hybrid `/app/wap/api/trade` 中 commerce 相关结构对齐，便于与后端 create / confirm / paywall 联调 */

/** ActivityMetaDTO（activity_api.thrift / paywall 共用） */
export interface IActivityMetaDTO {
    offer_id?: string;
    activity_id: string;
    activity_instance_id?: string;
    activity_template_id?: string;
    experiment_bucket?: string;
    valid_from?: number;
    valid_until?: number;
    remain_ms?: number;
    title?: string;
    /** ActivityNoType：1 首冲弹窗 / 2 取消自动订阅后弹窗 */
    activity_no?: number;
    /** true 本次激活；false 非本次激活 */
    is_activated_this_time?: boolean;
    discount_period?: number;
}

export interface IPayMethodDTO {
    store: string;
    extra_bonus: number;
    bonus?: string;
    final_bonus: number;
    source_type: 'STRIPE' | 'PAYPAL';
}

/**
 * 与 common.RightType 对齐。
 * 注意：order_api.thrift 里 RechargeProductDTO 注释仍写 Membership=2 为一次性会员，H5 以本枚举为准。
 */
export enum ERightType {
    Unspecified = 0,
    /** 单充加币 */
    Coin = 1,
    /** 自动续订订阅 */
    Membership = 2,
    /** 整本买断 */
    FullBook = 3,
    /** 一次性会员 */
    OneTimeMembership = 4,
}

/** 与 common.Period 对齐：时长种类，枚举值不是天数 */
export enum EPeriod {
    Week = 1,
    Month = 2,
    Year = 3,
    Day = 4,
    Quarter = 5,
    ThreeDay = 6,
}

/** 与 Period 同名；值为对应天数。禁止把 Period 的枚举数字当天数。 */
export enum EPeriodDays {
    Week = 7,
    Month = 30,
    Year = 365,
    Day = 1,
    Quarter = 90,
    ThreeDay = 3,
}

/** 付费墙 slots 展示位 key，与 common.PaywallSlot* 对齐 */
export const PAYWALL_SLOT = {
    TOP: 'top',
    BOTTOM: 'bottom',
} as const;

export interface IRechargeProductDTO {
    product_id: string;
    third_product_id: string;
    name: string;
    describtion?: string;
    price_cents: number;
    currency_sign: string;
    coins: number;
    bonus?: string;
    vouchers?: number;
    currency: string;
    offering_strategy?: string;
    activity_info?: IActivityMetaDTO;
    methods?: IPayMethodDTO[];
    is_recommend: boolean;
    /** 权益类型；slots 商品一定带 */
    right_type?: ERightType;
    /** 时长种类（Period）；RT=2 / RT=4 必有 */
    period?: EPeriod;
}

export interface ISubscribeProductDTO {
    product_id: string;
    third_product_id: string;
    name: string;
    discribtion?: string;
    tags?: string;
    period_sign?: string;
    price_cents: number;
    currency_sign: string;
    /** 订阅时长枚举名：'Day'（1 天）/ 'Week'（7 天）/ 'Month'（30 天）/ 'Quarter'（90 天）/ 'Year'（365 天） */
    vip_date: string;
    currency: string;
    offering_strategy?: string;
    activity_info?: IActivityMetaDTO;
    methods: IPayMethodDTO[];
    is_recommend: boolean;
    /** 权益类型；slots 商品一定带 */
    right_type?: ERightType;
    /** 时长种类（Period）；RT=2 / RT=4 必有 */
    period?: EPeriod;
    /** 优惠周期数；>=1 表示前 N 期订阅优惠（仅 RT=2 可能下发），前端折扣订阅判断不区分首期/多期 */
    discount_period_num?: number;
    /** 优惠价（美分）；与 discount_period_num 同时下发 */
    discount_price_cents?: number;
}

/** slots.top / slots.bottom 单项：充值或订阅 DTO */
export type TPaywallSlotProduct = IRechargeProductDTO | ISubscribeProductDTO;

export interface IPaywallDTO {
    /** 旧字段，仅存量端兼容；H5 只读 slots */
    recharge?: IRechargeProductDTO[];
    subscribe?: ISubscribeProductDTO[];
    /** key 为 PaywallSlot：top 轮播 / bottom 网格 */
    slots?: Record<string, TPaywallSlotProduct[]>;
}

export interface IGetPaywallDetailRequestBody {
    scene?: string;
    view_count?: number;
    source?: string;
    /** 阅读器拉墙时携带，供服务端计算整本动态价；前端不据此过滤 FullBook */
    trigger_drama_id?: string;
}

export interface IGetPaywallDetailData {
    paywall_id_str: string;
    version: number;
    paywall: IPaywallDTO;
    checksum: string;
    server_ts: number;
    promotion_id?: number;
    relation_id?: number;
    backup_level?: number;
}

export interface IClientProductParamsDTO {
    amount_string: string;
    currency_code: string;
    formatted_string: string;
}

export interface IStoreOffersStrategyDTO {
    offer_type?: string;
    offer_id?: string;
    eligibility?: string;
    discount_period_count?: number;
    discount_price_cents?: number;
    ineligibility_reason?: string;
}

export interface ICreateOrderRequestBody {
    product_id: string;
    paywall_id_str: string;
    version?: number;
    third_product_id?: string;
    client_params?: IClientProductParamsDTO;
    order_source?: string;
    trigger_drama_id?: string;
    order_from?: string;
    activity_id?: string;
    activity_instance_id?: string;
    paywall_checksum?: string;
    ts: string;
    view_count?: number;
    store_offers_strategy?: IStoreOffersStrategyDTO;
    store?: string;
    nonce?: string;
    sign?: string;
    /**
     * PayPal checkout-url 整页跳转下单时传入（订阅 + 一次性充值）。
     * 站内回跳 scene，不含前导 `/`（如 `reader`、`pay`、`discover`）。
     * 后端据此 + 附带的 drama / chapter 等参数拼回跳 URL。
     * `reader` 需同时传 trigger_drama_id / chapter_id。
     */
    return_scene?: string;
    /** 仅 return_scene 为阅读器路径时携带：当前屏幕章节 id */
    chapter_id?: string;
}

export interface IStorePayloadDTO {
    merchant_display_name: string;
    checkout_url: string;
    publishable_key: string;
    payment_intent_client_secret: string;
    customer_id: string;
    customer_ephemeral_key_secret: string;
    order_id: string;
    checkout_token: string;
    currency_code: string;
    amount: string;
    return_url: string;
    cancel_url: string;
    /** Stripe Checkout Session id（STRIPE_CHECKOUT） */
    checkout_session_id?: string;
}

export interface ICreateOrderData {
    order_id: string;
    third_product_id: string;
    product_id: string;
    activity_info?: IActivityMetaDTO;
    store?: string;
    store_order_id?: string;
    store_payload?: IStorePayloadDTO;
}

export interface IClientResultDTO {
    status: string;
    error_code?: number;
    error_type?: string;
    transaction_id?: string;
    original_transaction_id?: string;
    store: string;
    store_order_id?: string;
}

export interface IConfirmOrderRequestBody {
    order_id: string;
    client_result: IClientResultDTO;
    ts?: string;
}

export interface IConfirmOrderData {
    order_id: string;
    status: string;
    reason_code: string;
    retry_after_sec?: number;
    server_ts: number;
}
